/**
 * The only way this app talks to its API.
 *
 * Centralised so the rules in CLAUDE.md are properties of every call rather than things
 * each caller has to remember:
 *
 *   - `credentials: 'include'` so the HttpOnly session cookie rides along. The browser
 *     never holds a token; there is nothing here to attach to an Authorization header.
 *   - The antiforgery token travels in a header on every state-changing request. A
 *     cross-origin HTML form can POST but cannot set a header.
 *   - A 401 redirects to sign in ONCE. Never a retry loop — a silent retry against an
 *     expired session produces an invisible hammering of the login endpoint.
 *
 * Same origin throughout. There is no base URL and no CORS policy in this codebase.
 */

// ⚠️ The REQUEST token, not ASP.NET's cookie token. They are different values and are
// not interchangeable — reading the cookie token and echoing it is what caused every
// write to be rejected. The cookie token is HttpOnly and script never sees it.
import { clearRequestDraft } from "@/lib/request-draft";
import { clearTicketTabs } from "@/lib/ticket-tabs";

const CSRF_COOKIE = "__Host-ars-csrf-token";
const CSRF_HEADER = "X-CSRF-TOKEN";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * What to say when a failed response carries no problem document of its own.
 *
 * Plain sentences a person can act on. Nothing here names a server, a stack or an internal
 * type — the status is the only thing known, and it is all that is said.
 */
export function defaultTitleFor(status: number): string {
  switch (status) {
    case 403:
      return "You do not have access to this.";
    case 404:
      return "That could not be found.";
    case 408:
    case 504:
      return "The service took too long to answer.";
    case 429:
      return "Too many requests. Wait a moment and try again.";
    case 502:
    case 503:
      return "The service is not available right now.";
    default:
      return "Something went wrong.";
  }
}

/** Set once when a 401 has already triggered navigation, so concurrent calls do not stack redirects. */
let redirecting = false;

// ---------------------------------------------------------------------------
// Noticing that our own access changed.
//
// The server has always been right here and this bundle has always been stale. Permissions
// resolve per request against the database, so somebody demoted mid-session is refused from
// that moment — but the SPA reads `/api/me` once, at load, and goes on drawing the
// administration it no longer holds until the tab is reloaded. Nothing leaks: every one of
// those controls comes back 403 or 404. What is wrong is that the person cannot tell they
// were demoted, and a screen whose buttons all fail reads as a broken product rather than
// as changed access.
//
// So the server stamps every authenticated response with an opaque value that changes
// whenever anything deciding that caller's access changes, and this notices it move.
//
// ⚠️ NOT a security mechanism, and nothing may branch on the value. Forging, dropping or
// replaying the header buys one redundant refetch — which re-reads permissions from the
// database exactly like every other request.
// ---------------------------------------------------------------------------

const PERMISSION_STAMP_HEADER = "X-Permission-Stamp";

let lastPermissionStamp: string | null = null;

type PermissionChangeListener = () => void;

const permissionChangeListeners = new Set<PermissionChangeListener>();

/**
 * Registers a callback for "this user's access just changed". Returns an unsubscribe.
 *
 * The SessionProvider is the only subscriber and re-reads `/api/me`. It is an event rather
 * than a direct import so this module keeps knowing nothing about React.
 */
export function onPermissionsChanged(listener: PermissionChangeListener): () => void {
  permissionChangeListeners.add(listener);
  return () => permissionChangeListeners.delete(listener);
}

/**
 * Records the stamp the session was loaded with, so the next differing one is a change
 * rather than a first sighting.
 *
 * ⚠️ Called by the session load itself. Without it the very first API response after
 * startup looks like a change and fires a pointless second `/api/me`.
 */
export function primePermissionStamp(stamp: string | null): void {
  lastPermissionStamp = stamp;
}

function notePermissionStamp(response: Response): void {
  const stamp = response.headers.get(PERMISSION_STAMP_HEADER);

  // Absent on unauthenticated responses, on static files, and on any response that began
  // streaming before the header could be attached. Absence is not a change — treating it as
  // one would put every such response into a refetch loop.
  if (!stamp) return;

  if (lastPermissionStamp === null) {
    lastPermissionStamp = stamp;
    return;
  }

  if (lastPermissionStamp === stamp) return;

  lastPermissionStamp = stamp;

  // Fired before this call's own response is handled, so a screen re-rendering from fresh
  // permissions does not race the reply that revealed the change.
  for (const listener of permissionChangeListeners) listener();
}

/**
 * The antiforgery request token, as the SPA sees it.
 *
 * Exported because sign-out is a real form POST rather than a fetch — a navigation cannot
 * set a header, so the same value has to go into a hidden field.
 */
export function readCsrfToken(): string | null {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${CSRF_COOKIE}=`));

  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : null;
}

function isStateChanging(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS", "TRACE"].includes(method.toUpperCase());
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /**
   * A body to send as-is, for multipart uploads.
   *
   * Kept separate from `body` so JSON stays the default and nothing accidentally sends a
   * FormData through JSON.stringify — which produces "{}" and an empty upload that looks
   * like a server fault. The browser sets its own multipart boundary, so Content-Type is
   * deliberately not set here; the antiforgery header still is.
   */
  rawBody?: BodyInit;
  /** Idempotency key for creates. Travels as a header: it describes the request, not the resource. */
  idempotencyKey?: string;
}

export async function api<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);

  if (options.body !== undefined && options.rawBody === undefined) {
    headers.set("content-type", "application/json");
  }

  if (isStateChanging(method)) {
    const token = readCsrfToken();
    if (token) headers.set(CSRF_HEADER, token);
  }

  if (options.idempotencyKey) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }

  const response = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: "include",
    body:
      options.rawBody ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  });

  // Before the status checks, so a 403 caused by access that has just narrowed still
  // triggers the refresh that explains it.
  notePermissionStamp(response);

  if (response.status === 401) {
    // Redirect once, then stop. Several components can be loading at the same moment
    // and each will see this; without the guard they would all navigate.
    //
    // returnUrl brings the user back to the page they asked for rather than the home
    // screen. The server reduces it to a same-site path — a return URL is
    // attacker-controllable, and an open redirect out of a real sign-in is convincing
    // precisely because the sign-in genuinely happened on the real site.
    if (!redirecting) {
      redirecting = true;

      // ⚠️ Open-request tabs do not survive the session that opened them.
      //
      // They hold ticket numbers and subjects — a subject is free text somebody typed
      // about their problem — and these are shared clinical workstations. sessionStorage
      // already dies with the browser session; this covers the case that matters more,
      // where one person's session ends and another signs in at the same machine.
      clearTicketTabs();

      // ⚠️ And the half-written request, for exactly the same reason and more directly: a
      // draft is free text somebody typed about their own problem, and it must not be
      // sitting in the form when the next person signs in at this machine.
      clearRequestDraft();

      const returnUrl = window.location.pathname + window.location.search;
      window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    }

    throw new ApiError(401, "Your session has expired.");
  }

  if (!response.ok) {
    // The server sends RFC 7807 with a correlation id and no internals. Surface the
    // title, which is written to be shown to a person, and keep the id so a user can
    // quote it to the service desk.
    //
    // ⚠️ The fallback names the STATUS, never only "something went wrong". A refusal whose
    // body is not a problem document — a 403 from a proxy, a bare 502 from the edge, a
    // response somebody's browser extension rewrote — used to collapse into the same four
    // words as an unhandled exception, on the one screen where the difference decides what
    // a person should do next (sign in again, wait, or ring somebody).
    let title = defaultTitleFor(response.status);
    let correlationId: string | undefined;

    try {
      const problem = (await response.json()) as {
        title?: string;
        detail?: string;
        correlationId?: string;
      };

      // ⚠️ `detail` first, `title` as the fallback. A deliberate `Results.Problem(...)` writes
      // `detail` as the specific, actionable sentence — which field was rejected, which bound
      // was crossed, what to do instead — while `title` is the generic category shown beside
      // it ("That article could not be saved."). Dropping `detail` here meant every one of
      // those hand-written sentences across the product was thrown away before a person ever
      // saw it, leaving only the generic title to explain a 400. Safe to show unconditionally:
      // the *global* exception handler's own `detail` is always the same static fallback text,
      // never exception internals (see ProblemDetailsExceptionHandler) — only a specific,
      // hand-authored refusal ever puts anything more precise there.
      if (problem.detail) title = problem.detail;
      else if (problem.title) title = problem.title;
      correlationId = problem.correlationId;
    } catch {
      // Non-JSON error body. The status alone is all we can honestly report.
    }

    throw new ApiError(response.status, title, correlationId);
  }

  // ⚠️ Any bodyless response, not just 204.
  //
  // This checked 204 alone, and the first endpoint to answer 202 broke on it: parsing an
  // empty body throws a SyntaxError, which is not an ApiError, so every caller fell
  // through to its generic "something went wrong" message. The request had SUCCEEDED —
  // the directory sync was running happily — and the screen reported failure, which sent
  // us looking at the server for a fault that was here.
  //
  // 205 and 304 are bodyless by specification too. Content-Length: 0 catches the rest,
  // including a 200 that legitimately returns nothing.
  if (
    response.status === 204 ||
    response.status === 205 ||
    response.status === 304 ||
    response.headers.get("content-length") === "0"
  ) {
    return undefined as T;
  }

  // 202 and friends may or may not carry a body depending on the endpoint, so this reads
  // the text first and only parses when there is something to parse. Guessing from the
  // status code is what produced the bug above.
  const body = await response.text();
  if (body.length === 0) return undefined as T;

  return JSON.parse(body) as T;
}
